import "./style.scss";

import Close from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { TabContext, TabPanel } from "@mui/lab";
import { Avatar, Box, CircularProgress, Drawer, Tab, Tabs } from "@mui/material";
import ChannelOther from "assets/img/svg/ChannelOther";
import ShowStatements from "components/shared/BPShowStatements";
import BPSignedDocumentsTable from "components/shared/BPSignedDocumentsTable";
import BPTermDetail from "components/shared/BPTermDetail";
import ChannelIcon from "components/shared/ChannelIcon";
import EmptyState from "components/shared/EmptyState";
import InformationGrid from "components/shared/InformationGrid";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import LongTextBlock from "components/shared/LongTextBlock";
import StatementStatusChip from "components/shared/StatementStatusChip";
import CountryMarketData from "components/Title/CountryMarketData";
import { getCountryMarketData } from "components/Title/utils";
import { populateGridWithCustomFields } from "containers/CustomFieldsContainer/helpers";
import type { ITitle } from "containers/Titles/types";
import { formatLocaleDateString, formatNumber, isNewDate } from "helpers/utils";
import { useIsMobile } from "hooks/useIsMobile";
import { useIsPWA } from "hooks/usePWAMode";
import { toggleTheme } from "hooks/useTheme";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import type { ICustomField } from "store/customFieldsStore";
import { toggleTitleDrawer, type Currencies } from "store/settingsStore";
import type { AppDispatch, RootState } from "store/store";
import { fetchStatementsFromTitleBeneficiarPortal, fetchTitleByBeneficiar } from "store/titleStore";
import type { ILastStatement } from "store/types";

import variables from "../../assets/styles/_variables.module.scss";

/**
 * Title page of Beneficiary Portal
 */
const Title = (props: any) => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const location = useLocation();
    const { isMobile, isPhone } = useIsMobile();
    const isPWAMode = useIsPWA();

    const currentTitle = useSelector((state: RootState) => state.titles.beneficiarTitle) as ITitle;
    const statements = useSelector((state: RootState) => state.titles.beneficiarTitleStatement) as any[];
    const beneficiarTitleLoadingStatus = useSelector((state: RootState) => state.titles.beneficiarTitleStatus);
    const beneficiarCompagnies = useSelector((state: RootState) => state.beneficiary.beneficiarCompagnies);
    const companyAvatarUrl = beneficiarCompagnies.find((comp) => comp.id === currentTitle.companyId)?.avatarUrl;

    const countryMarketData = getCountryMarketData(currentTitle, "beneficiary");

    const [isOpen, setIsOpen] = useState(false);
    const [currentPeriodId, setCurrentPeriodId] = useState<number>();
    const [currentBeneficiarId, setCurrentBeneficiarId] = useState<number>();
    const [currentAdvancePaymentId, setCurrentAdvancePaymentId] = useState<number>();
    const [currentStatement, setCurrentStatement] = useState<ILastStatement | null>(null);
    const [value, setValue] = useState(location.state?.tab ?? "1");
    const [currentCompany, setCurrentCompany] = useState(
        beneficiarCompagnies.find((comp) => comp.id === currentTitle.companyId),
    );
    const [termIsOpen, setTermIsOpen] = useState<boolean>(false);
    const [currentTerm, setCurrentTerm] = useState<any>(null);

    /**
     * Array that contains custom fields created by company and for which the related resource is "beneficiary"
     */
    const companyTitleCustomFields: ICustomField[] | undefined =
        currentCompany?.customFields &&
        currentCompany?.customFields.filter((field) => field.relatedResource === "title");

    /**
     * Includes fields shown in Title details + custom fields (sections mixed)
     */
    const titleInfos = useMemo(() => {
        const displayedFields: Record<string, string> = {};

        // Default fields.
        if (currentTitle) {
            displayedFields[t("Titles.FormatType")] = currentTitle.formatTypeName
                ? currentTitle.formatTypeId != null
                    ? `${currentTitle.formatTypeName} #${currentTitle.formatTypeId}`
                    : currentTitle.formatTypeName
                : currentTitle.otherInformation?.format || "-";
            displayedFields[t("Titles.Pagination")] = currentTitle.otherInformation?.pagination || "-";
            displayedFields[t("Titles.Prize")] = currentTitle.otherInformation?.prize || "-";
        }

        // Add custom fields to the section (not including longstring)
        if (companyTitleCustomFields && currentTitle) {
            const fieldsWithoutLongstring = companyTitleCustomFields.filter(
                (field) => field.valueType !== "longstring",
            );
            populateGridWithCustomFields({
                resourceData: currentTitle,
                resourceCustomFields: fieldsWithoutLongstring,
                gridObject: displayedFields,
            });
        }

        return displayedFields;
    }, [companyTitleCustomFields]);

    /**
     * Includes all fields that are longstring (either native or custom)
     */
    const textAreaFields = useMemo(() => {
        // Default fields
        const displayedFields = [
            {
                title: t("NewTitle.Summary"),
                text: currentTitle?.otherInformation?.summary ? currentTitle?.otherInformation?.summary : "-",
            },
            // They probably don't want to show notes? If they have inner comments not meant to be shared. What do you think?
            // {
            //     title: t("NewTitle.Notes"),
            //     text: currentTitle?.otherInformation?.notes ? currentTitle?.otherInformation?.notes : "-",
            // },
        ];

        // Add custom fields to section
        if (companyTitleCustomFields) {
            const longStringFields = companyTitleCustomFields.filter((field) => field.valueType === "longstring");

            if (longStringFields && currentTitle?.customFields) {
                longStringFields.forEach((item) => {
                    const fieldData = currentTitle?.customFields?.find((field) => field.customFieldTypeId === item.id);
                    displayedFields.push({ title: item.name, text: fieldData?.value ? fieldData?.value : "-" });
                });
            }
        }

        return displayedFields;
    }, [companyTitleCustomFields]);

    const crealoInfos = currentTitle && {
        [t("Summary.CreationDate")]: currentTitle.creationDate
            ? formatLocaleDateString({ date: currentTitle.creationDate, options: { numeric: true, timeZone: "UTC" } })
            : "-",
        [t("Titles.LatestEdition")]: currentTitle.lastModifiedDate
            ? formatLocaleDateString({
                  date: currentTitle.lastModifiedDate,
                  options: { numeric: true, timeZone: "UTC" },
              })
            : "-",
    };

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    useEffect(() => {
        if (id) {
            if (currentTitle.id !== +id) dispatch(fetchTitleByBeneficiar(id));
            dispatch(fetchStatementsFromTitleBeneficiarPortal(+id));
        }
    }, [id, dispatch]);

    useEffect(() => {
        setCurrentCompany(beneficiarCompagnies.find((comp) => comp.id === currentTitle.companyId));
    }, [beneficiarCompagnies, currentTitle]);

    useEffect(() => {
        if (!location.state || !Object.keys(currentTitle).length) {
            return;
        }
        setCurrentTerm(currentTitle.terms?.find((term) => term.id === location.state.termId));
        setTermIsOpen(true);
    }, [location, currentTitle]);

    const toggleDrawer =
        (anchor: any, open: any): any =>
        (event: any) => {
            setCurrentPeriodId(undefined);
            if (
                event &&
                event.type === "keydown" &&
                ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
            ) {
                return;
            }
            setIsOpen(open);
            setTermIsOpen(open);
            setCurrentTerm(null);
        };

    const handleShowStatements = (statement: any) => {
        setCurrentPeriodId(statement.id);
        setCurrentBeneficiarId(statement.sliceId ? null : statement.benefciarId);
        setCurrentAdvancePaymentId(statement.sliceId ? statement.sliceId : null);
        setCurrentStatement(statement);
        setIsOpen(true);
    };

    const handleShowTerm = (term: any) => {
        setCurrentTerm(term);
        setTermIsOpen(true);
    };

    const titleStatementsColumns: LastElementTableColumns[] = [
        {
            id: "periodStart",
            label: t("BPStatement.Period"),
            align: "left",
            format: (value, id, row) => {
                if (value) {
                    return (
                        <div className="bp-title-statement-tab-date">
                            {`${formatLocaleDateString({ date: value })} - ${formatLocaleDateString({
                                date: row.periodEnd,
                            })}`}
                            <StatementStatusChip status={row.isActive} />
                        </div>
                    );
                } else return <>{t("BPStatement.Advance")}</>;
            },
        },
        {
            id: "grossDuty",
            label: t("StatementsTab.Gross"),
            align: "center",
            format: (value: any) =>
                formatNumber(value / 100, {
                    isCurrency: true,
                    always2Digit: true,
                    preferredCurrency: currentCompany?.currency as Currencies,
                }),
        },
        {
            id: "",
            label: "",
            align: "right",
            format: (value, id, row) =>
                !isMobile && (
                    <div className="title-bp-statement-link">
                        <button onClick={() => handleShowStatements(row)}>{t("Titles.Show")}</button>
                    </div>
                ),
        },
    ];

    const renderMobileStatementRow = (statement: any) => {
        return (
            <div className="list-mobile-element">
                <div className="element-left">
                    <div className="element-title-wrapper">
                        <Avatar src={currentTitle.avatarUrl} variant="rounded" />
                        <span className="element-title">{currentTitle.name}</span>
                    </div>
                    <span className="element-subtitle">{`${formatLocaleDateString({
                        date: statement.periodStart,
                    })} → ${formatLocaleDateString({ date: statement.periodEnd })}`}</span>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (isMobile) {
            if (isOpen || termIsOpen) toggleTheme("default");
            else toggleTheme("light-grey");
        }
    }, [isOpen, termIsOpen]);

    if (beneficiarTitleLoadingStatus === "pending") {
        return (
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    height: "100vh",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress sx={{ color: variables["primary-purple"] }} />
            </Box>
        );
    }

    if (!props.titles.length) {
        return (
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    height: "100vh",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <EmptyState type="authorTitles" />
            </Box>
        );
    }

    return beneficiarTitleLoadingStatus !== "succeeded" ? (
        <Box
            sx={{
                display: "flex",
                width: "100vh",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <CircularProgress sx={{ color: variables["primary-purple"] }} />
        </Box>
    ) : (
        <>
            {isMobile && (
                <>
                    <div className="title-bp-mobile-exit">
                        <div className="title-bp-mobile-exit-btn" onClick={() => dispatch(toggleTitleDrawer(false))}>
                            <ArrowBackIosIcon style={{ color: variables["foreground"] }} />
                            <span>{t("Titles.AllTitles")}</span>
                        </div>
                    </div>
                </>
            )}
            <div className="title-bp-container" style={isPWAMode ? { paddingBottom: "6.5rem" } : {}}>
                {currentStatement && (
                    <ShowStatements
                        isOpen={isOpen}
                        setOpen={setIsOpen}
                        toggleDrawer={toggleDrawer}
                        periodId={currentPeriodId}
                        term={{
                            ...currentStatement,
                            name: currentTitle.name,
                            avatarUrl: currentTitle.avatarUrl ?? "",
                            isPeriodActive: currentStatement?.isActive ?? 0,
                            companyId: currentTitle?.companyId,
                        }}
                        title={currentTitle.name}
                        beneficiarId={currentBeneficiarId}
                        advancePaymentId={currentAdvancePaymentId}
                        termId={currentStatement?.termId}
                        beneficiarCompanies={beneficiarCompagnies}
                    />
                )}
                {currentTitle && (
                    <TabContext value={value}>
                        <div className="title-bp-head">
                            <div className="title-bp-header">
                                <Avatar
                                    src={currentTitle.avatarUrl}
                                    sx={{
                                        color: "black",
                                        backgroundColor: "#FFF2F1",
                                        width: "80px",
                                        height: "80px",
                                        borderRadius: "8px",
                                    }}
                                    variant="rounded"
                                >
                                    {currentTitle.name.slice(0, 1)}
                                </Avatar>
                                <div className="title-bp-header-details">
                                    <div className="title-bp-header-base-data">
                                        <div className="title-bp-header-base-data-top">
                                            <h2>{currentTitle.name}</h2>
                                            <span>
                                                {t("BeneficiaryPortal.TitlePage.EAN13")} {currentTitle.ean13}
                                            </span>
                                            {!isMobile && (
                                                <span>
                                                    {currentTitle.releaseDate
                                                        ? `${t("Titles.PublishedOn")} ${formatLocaleDateString({
                                                              date: currentTitle.releaseDate,
                                                              options: { numeric: true, timeZone: "UTC" },
                                                          })} ${t("Titles.At")} `
                                                        : `${t("Titles.At").substring(0, 1).toLocaleUpperCase()}${t(
                                                              "Titles.At",
                                                          ).substring(1)} `}
                                                    {currentCompany && currentCompany.avatarUrl ? (
                                                        <img src={companyAvatarUrl} alt="company-logo" />
                                                    ) : (
                                                        ""
                                                    )}
                                                    {currentCompany?.name ?? ""}
                                                </span>
                                            )}
                                            {isMobile && (
                                                <>
                                                    {currentTitle.releaseDate && (
                                                        <span>{`${t("Titles.PublishedOn")} ${formatLocaleDateString({
                                                            date: currentTitle.releaseDate,
                                                            options: { numeric: true, timeZone: "UTC" },
                                                        })}`}</span>
                                                    )}
                                                    {currentCompany && (
                                                        <div className="title-at-company">
                                                            <span>{t("Titles.At")}</span>
                                                            {currentCompany.avatarUrl ? (
                                                                <img src={companyAvatarUrl} alt="company-logo" />
                                                            ) : (
                                                                <span>&nbsp;</span>
                                                            )}
                                                            <span>{currentCompany?.name}</span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="title-bp-tabs">
                                <Tabs
                                    TabIndicatorProps={{
                                        style: { background: "#141516", textTransform: "none" },
                                    }}
                                    value={value}
                                    onChange={handleChange}
                                    textColor="inherit"
                                    sx={{ "& .MuiTab-root": { marginRight: "32px" } }}
                                >
                                    <Tab label={t("Titles.GeneralInformation")} value="1" />
                                    <Tab
                                        label={t("Titles.Contract", { count: currentTitle.terms?.length ?? 1 })}
                                        value="2"
                                    />
                                    <Tab label={t("Titles.Statements")} value="3" />
                                    <Tab label={t("BPTitle.TabSignedDocuments")} value="4" />
                                </Tabs>
                            </div>
                        </div>
                        <TabPanel value="1" sx={isMobile ? { padding: 0 } : { padding: "0px" }}>
                            <div className="title-bp-general-info">
                                <InformationGrid
                                    data={titleInfos}
                                    title={t("Titles.Specification")}
                                    portal={"author"}
                                />
                                <div className="tab-content-bloc">
                                    <h3 className="information-title">
                                        {currentTitle.channels && currentTitle.channels.length
                                            ? t("Titles.PricePerChannel")
                                            : t("BPDashboard.Price")}
                                    </h3>
                                    <table className="tab-content-price-channels-table">
                                        <tbody>
                                            <tr>
                                                <td>
                                                    {!isPhone && (
                                                        <span className="tab-content-price-channels-kind">
                                                            {<ChannelOther />}
                                                            {t("Titles.ByDefault")}
                                                        </span>
                                                    )}
                                                    {isPhone && (
                                                        <div className="tab-content-price-channels-mobile-info">
                                                            <span className="tab-content-price-channels-kind">
                                                                {<ChannelOther />}
                                                                <div className="tab-content-name-ean13">
                                                                    <span>{t("Titles.ByDefault")}</span>
                                                                    <div className="tab-content-ean13-wrapper">
                                                                        <span className="tab-content-ean13">
                                                                            {t("BeneficiaryPortal.TitlePage.EAN13")}
                                                                        </span>
                                                                        <span>{currentTitle.ean13}</span>
                                                                    </div>
                                                                </div>
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    {!isPhone && (
                                                        <>
                                                            <span className="tab-content-ean13">
                                                                {t("BeneficiaryPortal.TitlePage.EAN13")}
                                                            </span>
                                                            <span>{currentTitle.ean13}</span>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    <span>
                                                        {formatNumber(Math.round(+currentTitle.price) / 100, {
                                                            isCurrency: true,
                                                            always2Digit: true,
                                                            preferredCurrency: currentTitle.currency,
                                                        })}
                                                    </span>
                                                </td>
                                            </tr>
                                            {currentTitle.channels &&
                                                (currentTitle.channels as any).map((ch: any, i: number) => {
                                                    const hasCountryMarketData =
                                                        !!countryMarketData?.countryMarketChannelById[ch.id]?.length;
                                                    const isMainChannel = !ch.countryMarket;

                                                    if (isMainChannel) {
                                                        return (
                                                            <>
                                                                <tr
                                                                    key={`channels-prices-${ch.id}`}
                                                                    style={
                                                                        hasCountryMarketData ? { borderBottom: 0 } : {}
                                                                    }
                                                                >
                                                                    <td
                                                                        style={
                                                                            hasCountryMarketData
                                                                                ? { paddingBottom: 0 }
                                                                                : {}
                                                                        }
                                                                    >
                                                                        <span className="tab-content-price-channels-kind">
                                                                            <ChannelIcon channelKind={ch.kind} />
                                                                            <div className="starting-channel-name">
                                                                                {ch.channelName ?? (
                                                                                    <span className="not-defined">
                                                                                        {t("Titles.NotDefined")}
                                                                                    </span>
                                                                                )}
                                                                                {ch.name ? (
                                                                                    <div className="overriden-channel-data">
                                                                                        {ch.name}
                                                                                    </div>
                                                                                ) : (
                                                                                    ""
                                                                                )}
                                                                                {isPhone && (
                                                                                    <div className="tab-content-ean13-wrapper">
                                                                                        <span className="tab-content-ean13">
                                                                                            {t(
                                                                                                "BeneficiaryPortal.TitlePage.EAN13",
                                                                                            )}
                                                                                        </span>
                                                                                        <span>{ch.ean13}</span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </span>
                                                                    </td>
                                                                    <td
                                                                        style={
                                                                            hasCountryMarketData
                                                                                ? { paddingBottom: 0 }
                                                                                : {}
                                                                        }
                                                                    >
                                                                        {!isPhone && (
                                                                            <>
                                                                                <span className="tab-content-ean13">
                                                                                    {t(
                                                                                        "BeneficiaryPortal.TitlePage.EAN13",
                                                                                    )}
                                                                                </span>
                                                                                <span>{ch.ean13}</span>
                                                                                {ch.releaseDate ? (
                                                                                    <div className="overriden-channel-data">
                                                                                        {formatLocaleDateString({
                                                                                            date: ch.releaseDate,
                                                                                            options: {
                                                                                                numeric: true,
                                                                                                timeZone: "UTC",
                                                                                            },
                                                                                        })}
                                                                                    </div>
                                                                                ) : (
                                                                                    ""
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </td>
                                                                    <td
                                                                        style={
                                                                            hasCountryMarketData
                                                                                ? { paddingBottom: 0 }
                                                                                : {}
                                                                        }
                                                                    >
                                                                        <span>
                                                                            {formatNumber(Math.round(+ch.price) / 100, {
                                                                                isCurrency: true,
                                                                                always2Digit: true,
                                                                                preferredCurrency: ch.currency,
                                                                            })}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                                {hasCountryMarketData && (
                                                                    <CountryMarketData
                                                                        isMobile={isMobile}
                                                                        companyCountry={currentCompany?.countryCode}
                                                                        data={
                                                                            countryMarketData?.countryMarketChannelById[
                                                                                ch.id
                                                                            ]
                                                                        }
                                                                        companyCurrency={currentTitle?.currency}
                                                                    />
                                                                )}
                                                            </>
                                                        );
                                                    }
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                                {textAreaFields.map((field) => (
                                    <LongTextBlock
                                        text={field.text}
                                        title={field.title}
                                        key={`title.longstring.${field.title.trim()}`}
                                    />
                                ))}
                                <InformationGrid
                                    data={crealoInfos}
                                    title={t("Titles.Informations")}
                                    portal={"author"}
                                />
                            </div>
                        </TabPanel>

                        <TabPanel value="2" style={{ padding: "0px" }}>
                            {currentTitle.terms?.length ? (
                                currentTitle.terms.length > 1 ? (
                                    <div className="title-bp-terms">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>
                                                        {isMobile
                                                            ? t("Terms.BeneficiaryOrRepresentativeShort")
                                                            : t("Terms.BeneficiaryOrRepresentative")}
                                                    </th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentTitle.terms.map((term, i) => {
                                                    return (
                                                        <tr
                                                            key={`terms-${i}`}
                                                            onClick={() => (isMobile ? handleShowTerm(term) : {})}
                                                        >
                                                            <td className="author-name">
                                                                {term.representative
                                                                    ? term.representative
                                                                    : `${term.surname.toUpperCase()} ${
                                                                          term.name ?? ""
                                                                      }`}
                                                            </td>
                                                            <td>
                                                                <div>
                                                                    <button onClick={() => handleShowTerm(term)}>
                                                                        {t("Terms.Visit")}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <BPTermDetail
                                        term={currentTitle.terms[0]}
                                        companies={beneficiarCompagnies}
                                        title={currentTitle}
                                    />
                                )
                            ) : (
                                <EmptyState type="generic" sx={{ margin: "56px auto 0" }} />
                            )}
                            <Drawer
                                anchor={isMobile ? "bottom" : "right"}
                                className="bp-show-term"
                                open={termIsOpen}
                                onClose={toggleDrawer("right", false)}
                            >
                                <div className="bp-show-term-container">
                                    <Close
                                        className="bp-show-term-close"
                                        onClick={() => {
                                            setTermIsOpen(false);
                                            setCurrentTerm(null);
                                        }}
                                    />
                                    {currentTerm && (
                                        <BPTermDetail
                                            term={currentTerm}
                                            companies={beneficiarCompagnies}
                                            title={currentTitle}
                                        />
                                    )}
                                </div>
                            </Drawer>
                        </TabPanel>

                        <TabPanel value="3" style={{ padding: "0px" }}>
                            <div className="title-bp-statements">
                                {statements.length ? (
                                    <LastElementTable
                                        isMobile={isMobile}
                                        renderMobileRow={(element) => renderMobileStatementRow(element)}
                                        columns={titleStatementsColumns}
                                        data={statements}
                                        bottomFooterContent={<></>}
                                        isNewDate={(row: any, index: number, data: any) =>
                                            isNewDate(row, index, data, "periodEnd")
                                        }
                                        dataType="statements"
                                        onRowClick={(id: any, id2: any, type: any, row: any) =>
                                            handleShowStatements(row)
                                        }
                                    />
                                ) : (
                                    <EmptyState sx={{ margin: "0 auto" }} type="generic" />
                                )}
                            </div>
                        </TabPanel>

                        <TabPanel value="4" style={{ padding: "24px" }}>
                            <BPSignedDocumentsTable titleId={currentTitle.id} />
                        </TabPanel>
                    </TabContext>
                )}
            </div>
        </>
    );
};

export default Title;
